import { Observable } from "rxjs";
import { Attachment } from "../attachments/attachments.interface";
import { CodeName, TimestampCustom } from "../common.http.interface";
import { ApplicantIndividual, ApplicantIndividualContact, ApplicantIndividualIdentification } from "../applicants/applicants.interface";

export enum ExceptionStatus {
    pending = 'Pending',
    attention = 'Attention',

    validating = 'Validating',
    validated = 'Validated',

    revision = 'Revision',
    revisionN2 = 'Revision N2',

    pre_accepted = 'Pre-Accepted',
    accepted = 'Accepted',

    pre_rejected = 'Pre-Rejected',
    rejected = 'Rejected',
    expired = 'Expired'
}

export interface Exception {
    applicantIdentifier: string;
    applicantName: string;
    attachments: Attachment[];
    attachmentsOperator: Attachment[];
    exceptionIdentifier: string;
    municipality: string;
    municipalities: string[];
    state: string;
    states: {
        state: string;
        ticket: {
            timestamp: TimestampCustom;
            sourceUser: string;
            sourceProfile: string;
            destinationProfile: string;
            observations: string;
            foreground: string;
            background: string;
        };
        timestamp: TimestampCustom;
    }[];
    recordNumber: string;
    timestamps: {
        creationTimestamp: TimestampCustom;
        modificationTimestamp: TimestampCustom;
        validTo?: TimestampCustom;
    };
    type: {
        code: string;
        name?: string;
        options?: {
            code: string;
        }[];
    }
    type_malalt?: {
        medicalCenter: string,
        selfResponsibility: boolean,
        TSI: string,
        vehicleOwner?: ApplicantIndividual
    },
    type_municipal?: {
        address?: ApplicantIndividualContact,
        description: string,
        eventType: string,
        from: TimestampCustom,
        municipality: string,
        to: TimestampCustom
    },
    type_prof?: {
        selfResponsibility: boolean,
    },
    type_singular?: {
        serviceType: string,
    },
    type_tratmed?: {
        from: TimestampCustom,
        medicalCenter: string,
        selfResponsibility: boolean,
        to: TimestampCustom,
        TSI: string,
        vehicleOwner?: ApplicantIndividual
    },
    type_vpmr?: {
        anotherPerson: boolean,
        applicant?: ApplicantIndividualIdentification,
        collective: boolean
    },
    type_salary?: {
        hasRelation: boolean,
        civilState: string,
        family: number
    },
    vehiclePlate: string;
    observation?: string;
    operator?: string;
}

export interface ExceptionResponse {
    elements: Exception[];
    limit: number;
    offset: number;
    page: number;
    pages: number;
    total: number;
}

export interface ExceptionCreate {
    type: {
        code: string;
        options?: {
            code?: string;
            name?: string;
        }[];
    };
    municipality: string;
    municipalities: string[];
    validFrom?: TimestampCustom;
    attachments?: Attachment[];
    observations?: string;
    vehicles?: VehicleExcel[];
    type_malalt?: {
        medicalCenter: string,
        selfResponsibility: boolean,
        TSI: string,
        vehicleOwner?: ApplicantIndividual
    },
    type_municipal?: {
        contact?: ApplicantIndividualContact,
        description: string,
        eventType: string,
        from: TimestampCustom,
        municipality: string,
        to: TimestampCustom
    },
    type_prof?: {
        selfResponsibility: boolean,
    },
    type_singular?: {
        serviceType: string,
    },
    type_tratmed?: {
        from: TimestampCustom,
        medicalCenter: string,
        selfResponsibility: boolean,
        to: TimestampCustom,
        TSI: string,
        vehicleOwner?: ApplicantIndividual
    },
    type_vpmr?: {
        anotherPerson: boolean,
        applicant?: ApplicantIndividualIdentification,
        collective: boolean
    },
    type_salary?: {
        hasRelation: boolean,
        civilState: string,
        family: number
    },
    type_foreign?: {
        fuel: {
            code: string
        },
        classification: {
            code: string
        },
        year: number
    },
    flags?: {
        email: boolean,
        sms: boolean,
        personal: boolean,
        privacity: boolean,
        veracity: boolean,
        protection: boolean,
        vehicleDocumentation: boolean
    }
}

export interface StreetTypesResponse {
    streets: CodeName[];
}

export interface VehicleExcel {
    vehiclePlate: string;
    vehicleAlias?: string;
    errorMessage?: string;
    correctMessage?: string;
}


export interface IExceptionsService {

    getExceptionsUser(limit: number, offset: number): Observable<ExceptionResponse>;
    getExceptionsUserByApplicant(applicantId: string, plate: string, limit: number, offset: number): Observable<ExceptionResponse>;
    getExceptionByApplicant(applicantId: string, exceptionId: string, plate: string): Observable<Exception>;
    // returns observable that resolves to list of Exceptions
    getExceptions(plate: string, limit: number, offset: number): Observable<ExceptionResponse>;

    // returns observable that resolves to list of Exceptions
    getException(exceptionId: string, plate: string): Observable<Exception>;

    // returns observable that resolves to ID of new Exception
    addException(plate: string, newException: ExceptionCreate): Observable<number>;
    addExceptionByApplicant(plate: string, documentNumber: string, newException: ExceptionCreate): Observable<number>;
    // returns observable that resolves to status message
    updateException(exceptionId: string, plate: string, exceptionToUpdate: ExceptionCreate): Observable<Exception>;
    updateExceptionByApplicant(exceptionId: string, plate: string, documentNumber: string, exceptionToUpdate: ExceptionCreate): Observable<Exception>
    // returns observable that resolves to status message
    deleteException(exceptionId: string, plate: string): Observable<string>;

    getSteetTypes(): Observable<StreetTypesResponse>;

    // multiple
    downloadMultipleExcel(): Observable<Blob>;
    uploadMultipleExcel(file: Attachment): Observable<VehicleExcel[]>;
    addMultipleException(newException: ExceptionCreate): Observable<VehicleExcel[]>;
}